/**
 * HOW LONG A CATEGORY WAS PLANNED TO TAKE, AND HOW LONG IT ACTUALLY TOOK.
 *
 * Asked for on 1 Oct 2026, for analysis: «в проведених категоріях давай
 * показувати, скільки часу було витрачено, і скільки планувалося… а в не
 * проведених, показувати, скільки часу планується».
 *
 * Both answers come from data the floor payload already carries — the schedule
 * slot (`planned.startTime` / `planned.endTime`, «HH:MM» on the competition day)
 * and the mat's own record (`actual.startedAt` / `actual.finishedAt`, ISO
 * timestamps). Nothing here guesses: a missing or malformed half returns null,
 * and the caller renders «—» rather than a zero that reads as «instant».
 *
 * Pure functions in utils rather than inline in the card, because the
 * arithmetic is the part that can be wrong while the screen still looks right,
 * and it is read by two screens (the chief secretary's floor and the mat).
 */

const HHMM = /^(\d{1,2}):(\d{2})$/;

/** Minutes since midnight for «HH:MM», or null. */
const minutesOfDay = value => {
    if ('string' !== typeof value) {
        return null;
    }
    const match = HHMM.exec(value.trim());
    if (!match) {
        return null;
    }
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59) {
        return null;
    }

    return hours * 60 + minutes;
};

/**
 * The minutes a schedule slot reserves for the category.
 *
 * Null when either end is missing or the slot does not describe a forward span
 * — a planned duration of zero or less is a broken slot, not a fast category,
 * and printing «0 хв» beside a real elapsed time would invite the wrong
 * conclusion.
 *
 * @param {{startTime?: string, endTime?: string}|null|undefined} planned
 * @returns {number|null}
 */
export const plannedMinutes = planned => {
    if (!planned) {
        return null;
    }
    const from = minutesOfDay(planned.startTime);
    const to = minutesOfDay(planned.endTime);
    if (null === from || null === to || to <= from) {
        return null;
    }

    return to - from;
};

/**
 * The minutes the mat actually spent on the category.
 *
 * Null until BOTH ends exist: a category still running has a start and no
 * finish, and «started 40 minutes ago» is a different statement from «took 40
 * minutes» — the first changes every second, and this is read for analysis
 * afterwards.
 *
 * Null too for a category conducted from the bracket screen, which never
 * touches its mat row and so has no start at all. That is the common case on a
 * competition run from the admin side, and it must read «—», not «0».
 *
 * Rounded to the nearest minute: the schedule is in whole minutes, so a
 * comparison in seconds would be a precision the planned half does not have.
 *
 * @param {{startedAt?: string, finishedAt?: string}|null|undefined} actual
 * @returns {number|null}
 */
export const actualMinutes = actual => {
    if (!actual || !actual.startedAt || !actual.finishedAt) {
        return null;
    }
    const from = Date.parse(actual.startedAt);
    const to = Date.parse(actual.finishedAt);
    if (Number.isNaN(from) || Number.isNaN(to) || to < from) {
        return null;
    }

    return Math.round((to - from) / 60000);
};
